import React, { useState, useEffect, useRef } from 'react';
import { aiService } from '../services/api';
import '../styles/AIPanel.css';

export default function AIPanel({ investigationId, investigation }) {
  const [aiStatus, setAiStatus]       = useState(null);
  const [activeMode, setActiveMode]   = useState('query'); // 'query' | 'analyze' | 'report'
  const [question, setQuestion]       = useState('');
  const [analyzeAddr, setAnalyzeAddr] = useState('');
  const [messages, setMessages]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [report, setReport]           = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    aiService.status()
      .then(res => setAiStatus(res.data))
      .catch(() => setAiStatus(null));
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Pre-fill analyze address from investigation start address (once) ──
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (!prefilledRef.current && investigation?.start_address) {
      prefilledRef.current = true;
      setAnalyzeAddr(investigation.start_address);
    }
  }, [investigation]);

  const pushMessage = (role, content, meta = {}) => {
    setMessages(prev => [...prev, { role, content, meta, ts: new Date() }]);
  };

  // ── Query investigation ──
  const handleQuery = async () => {
    if (!question.trim() || loading) return;
    const q = question.trim();
    setQuestion('');
    pushMessage('user', q);
    setLoading(true);
    try {
      const res = await aiService.queryInvestigation(investigationId, q);
      pushMessage('ai', res.data.answer);
    } catch (err) {
      pushMessage('error', err.response?.data?.detail || 'Query failed. Is the AI provider configured?');
    } finally {
      setLoading(false);
    }
  };

  const handleQueryKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleQuery();
    }
  };

  // ── Analyze address ──
  const handleAnalyze = async () => {
    if (!analyzeAddr.trim() || loading) return;
    const addr = analyzeAddr.trim();
    const blockchain = investigation?.blockchain || 'tron';
    setLoading(true);
    try {
      const res = await aiService.analyzeAddress(blockchain, addr);
      setMessages(prev => [...prev, {
        role: 'analysis',
        address: addr,
        risk: res.data.risk,
        content: res.data.ai_analysis,
        ts: new Date(),
      }]);
    } catch (err) {
      pushMessage('error', err.response?.data?.detail || 'Analysis failed.');
    } finally {
      setLoading(false);
    }
  };

  // ── Generate report ──
  const handleGenerateReport = async () => {
    if (reportLoading) return;
    setReportLoading(true);
    setReport(null);
    try {
      const res = await aiService.generateReport(investigationId);
      setReport(res.data);
    } catch (err) {
      pushMessage('error', err.response?.data?.detail || 'Report generation failed.');
    } finally {
      setReportLoading(false);
    }
  };

  const copyReport = () => {
    if (report?.report) navigator.clipboard.writeText(report.report);
  };

  const getRiskColor = (level) => {
    const map = { LOW: '#4caf50', MEDIUM: '#ff9800', HIGH: '#ff5722', CRITICAL: '#f44336' };
    return map[level] || '#888';
  };

  // ── Render ──
  return (
    <div className="ai-panel">

      {/* Header + status */}
      <div className="ai-header">
        <div className="ai-title">
          <span className="ai-icon">🤖</span>
          <h3>AI Assistant</h3>
        </div>
        {aiStatus && (
          <div className={`ai-status-pill ${aiStatus.key_set ? 'connected' : 'disconnected'}`}>
            <span className="status-dot" />
            {aiStatus.key_set
              ? `${aiStatus.provider} · ${aiStatus.model}`
              : 'No API key — run Claude/START.bat'}
          </div>
        )}
      </div>

      {/* Mode tabs */}
      <div className="ai-mode-tabs">
        {[
          { id: 'query',   label: '💬 Ask Questions' },
          { id: 'analyze', label: '🔍 Analyze Address' },
          { id: 'report',  label: '📄 Generate Report' },
        ].map(m => (
          <button
            key={m.id}
            className={`ai-mode-btn ${activeMode === m.id ? 'active' : ''}`}
            onClick={() => setActiveMode(m.id)}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* ── Query Mode ── */}
      {activeMode === 'query' && (
        <div className="ai-query-mode">
          <div className="ai-messages">
            {messages.length === 0 && (
              <div className="ai-empty">
                <p>Ask anything about this investigation.</p>
                <div className="ai-suggestions">
                  {[
                    'What are the most suspicious addresses?',
                    'Are there any circular transaction patterns?',
                    'Summarise the key findings so far.',
                    'Which address has the highest risk score?',
                  ].map(s => (
                    <button key={s} className="suggestion-chip"
                      onClick={() => setQuestion(s)}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`ai-message ${msg.role}`}>
                {msg.role === 'analysis' ? (
                  <div className="analysis-card">
                    <div className="analysis-header">
                      <code className="analysis-addr">{msg.address}</code>
                      {msg.risk && (
                        <span className="risk-badge"
                          style={{ color: getRiskColor(msg.risk.risk_level),
                                   borderColor: getRiskColor(msg.risk.risk_level) }}>
                          {msg.risk.risk_level} · {msg.risk.risk_score}/100
                        </span>
                      )}
                    </div>
                    <p className="analysis-text">{msg.content}</p>
                  </div>
                ) : (
                  <>
                    <div className="msg-label">
                      {msg.role === 'user' ? '👤 You' : msg.role === 'error' ? '⚠ Error' : '🤖 AI'}
                    </div>
                    <div className="msg-content">{msg.content}</div>
                  </>
                )}
              </div>
            ))}

            {loading && (
              <div className="ai-message ai">
                <div className="msg-label">🤖 AI</div>
                <div className="ai-typing">
                  <span /><span /><span />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="ai-input-row">
            <textarea
              className="ai-input"
              value={question}
              onChange={e => setQuestion(e.target.value)}
              onKeyDown={handleQueryKey}
              placeholder="Ask a question… (Enter to send, Shift+Enter for newline)"
              rows={2}
              disabled={loading}
            />
            <button className="ai-send-btn" onClick={handleQuery}
              disabled={loading || !question.trim()}>
              {loading ? '⏳' : '➤'}
            </button>
          </div>
        </div>
      )}

      {/* ── Analyze Mode ── */}
      {activeMode === 'analyze' && (
        <div className="ai-analyze-mode">
          <p className="mode-description">
            Get an AI risk explanation for any address in this investigation.
          </p>
          <div className="analyze-input-row">
            <input
              type="text"
              className="analyze-addr-input"
              value={analyzeAddr}
              onChange={e => setAnalyzeAddr(e.target.value)}
              placeholder={investigation?.blockchain === 'tron' ? 'T... (TRON address)' : '0x... (address)'}
            />
            <button className="ai-action-btn" onClick={handleAnalyze}
              disabled={loading || !analyzeAddr.trim()}>
              {loading ? '⏳ Analysing…' : '🔍 Analyse'}
            </button>
          </div>

          <div className="ai-messages analyze-results">
            {messages.filter(m => m.role === 'analysis' || m.role === 'error').length === 0 && (
              <div className="ai-empty">
                <p>Enter an address above to get an AI-powered risk breakdown.</p>
              </div>
            )}
            {messages.filter(m => m.role === 'analysis' || m.role === 'error').map((msg, i) => (
              <div key={i} className={`ai-message ${msg.role}`}>
                {msg.role === 'analysis' ? (
                  <div className="analysis-card">
                    <div className="analysis-header">
                      <code className="analysis-addr">{msg.address}</code>
                      {msg.risk && (
                        <span className="risk-badge"
                          style={{ color: getRiskColor(msg.risk.risk_level),
                                   borderColor: getRiskColor(msg.risk.risk_level) }}>
                          {msg.risk.risk_level} · {msg.risk.risk_score}/100
                        </span>
                      )}
                    </div>
                    {msg.risk?.factors && Object.keys(msg.risk.factors).length > 0 && (
                      <div className="risk-factors">
                        {Object.keys(msg.risk.factors).map(f => (
                          <span key={f} className="risk-factor-chip">{f.replace(/_/g, ' ')}</span>
                        ))}
                      </div>
                    )}
                    <p className="analysis-text">{msg.content}</p>
                  </div>
                ) : (
                  <>
                    <div className="msg-label">⚠ Error</div>
                    <div className="msg-content">{msg.content}</div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Report Mode ── */}
      {activeMode === 'report' && (
        <div className="ai-report-mode">
          <p className="mode-description">
            Generate a full markdown investigation report based on all transactions,
            addresses, risk scores, and notes.
          </p>
          <button className="ai-action-btn generate-btn"
            onClick={handleGenerateReport} disabled={reportLoading}>
            {reportLoading ? '⏳ Generating report…' : '📄 Generate Report'}
          </button>

          {report && (
            <div className="report-output">
              <div className="report-toolbar">
                <span className="report-meta">
                  Generated {new Date(report.generated_at).toLocaleString()}
                </span>
                <button className="copy-btn" onClick={copyReport}>📋 Copy</button>
              </div>
              <pre className="report-markdown">{report.report}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
